import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Box, AppBar, Toolbar, IconButton, Typography, Chip, Button,
  Fab, Snackbar, Alert, Stack, Menu, MenuItem,
} from '@mui/material';
import {
  ArrowBack, MyLocation, AddLocation, Download, MoreVert,
  GpsFixed, GpsNotFixed, SatelliteAlt,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import {
  projekteAPI, baeumeAPI, schadenAPI, presetsAPI, exportAPI,
} from '../services/api';
import { useGPS, GPSPosition } from '../services/gps';
import ErfassungsDialog, {
  BaumFormData, SchadenData, emptyForm,
} from '../components/ErfassungsDialog';

// Default Leaflet-Icons (sonst hässlich)
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl:       'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl:     'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Eigenes Icon für eigene Position
const ownPositionIcon = L.divIcon({
  className: 'own-position',
  html: '<div style="width:18px;height:18px;border-radius:50%;background:#4a7c59;border:3px solid white;box-shadow:0 0 8px rgba(0,0,0,0.4)"></div>',
  iconSize: [18, 18],
  iconAnchor: [9, 9],
});

const treeIcon = L.divIcon({
  className: 'tree-marker',
  html: '<div style="font-size:24px">🌳</div>',
  iconSize: [24, 24],
  iconAnchor: [12, 12],
});

// Hilfs-Komponente: bewegt die Karte zur aktuellen Position
function CenterMap({ position }: { position: GPSPosition | null }) {
  const map = useMap();
  useEffect(() => {
    if (position) {
      map.setView([position.latitude, position.longitude], map.getZoom() < 16 ? 18 : map.getZoom());
    }
  }, [position?.latitude, position?.longitude]);
  return null;
}

export default function ProjektDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const gps = useGPS();
  const [projekt, setProjekt] = useState<any>(null);
  const [baeume, setBaeume] = useState<any[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{ text: string; severity: 'success' | 'error' } | null>(null);
  const [error, setError] = useState('');
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

  // Merkfunktion State
  const [pinned, setPinned] = useState<Set<keyof BaumFormData>>(new Set());
  const [lastForm, setLastForm] = useState<BaumFormData>(emptyForm());  // Auto-Memory
  const [nextNummer, setNextNummer] = useState<string>('');
  const [presets, setPresets] = useState<any[]>([]);
  const [activePreset, setActivePreset] = useState<any | null>(null);

  // Daten laden
  const load = useCallback(async () => {
    if (!id) return;
    try {
      const [p, b, ps] = await Promise.all([
        projekteAPI.get(id),
        baeumeAPI.list(id),
        presetsAPI.list(id),
      ]);
      setProjekt(p);
      setBaeume(b);
      setPresets(ps);
    } catch (e: any) {
      setError(e.response?.data?.error || 'Daten konnten nicht geladen werden');
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // GPS automatisch starten
  useEffect(() => {
    gps.start();
    return () => gps.stop();
  }, []);

  // Nächste Baumnummer holen wenn Bäume oder Projekt sich ändern
  useEffect(() => {
    if (!id) return;
    baeumeAPI.nextNummer(id).then((r) => setNextNummer(r.suggestion)).catch(() => {});
  }, [id, baeume.length]);

  // Mittelpunkt der Karte: GPS-Position oder Mittelwert der Bäume oder Deutschland-Mitte
  const mapCenter = useMemo<[number, number]>(() => {
    if (gps.position) return [gps.position.latitude, gps.position.longitude];
    if (baeume.length > 0) {
      const valid = baeume.filter((b) => b.latitude && b.longitude);
      if (valid.length > 0) {
        const lat = valid.reduce((s, b) => s + Number(b.latitude), 0) / valid.length;
        const lng = valid.reduce((s, b) => s + Number(b.longitude), 0) / valid.length;
        return [lat, lng];
      }
    }
    return [51.5, 10.5];   // Deutschland-Mitte
  }, [gps.position, baeume]);

  // Initial-Form für Erfassungs-Dialog: Auto-Memory + gepinnte Werte + Preset + nächste Nummer
  const initialForm = useMemo<BaumFormData>(() => {
    const base: BaumFormData = emptyForm();
    // Gepinnte Felder aus lastForm übernehmen
    for (const field of pinned) {
      (base as any)[field] = (lastForm as any)[field];
    }
    // Preset überschreibt
    if (activePreset?.daten) {
      Object.assign(base, activePreset.daten);
    }
    // Baumnummer auto-vorschlagen
    if (!base.baumnummer) base.baumnummer = nextNummer;
    return base;
  }, [pinned, lastForm, activePreset, nextNummer]);

  const handleOpenDialog = () => {
    if (!gps.position) {
      setSnackbar({ text: 'Bitte warte auf GPS-Position…', severity: 'error' });
      return;
    }
    setDialogOpen(true);
  };

  const handlePinChange = (field: keyof BaumFormData, isPinned: boolean) => {
    setPinned((p) => {
      const n = new Set(p);
      if (isPinned) n.add(field); else n.delete(field);
      return n;
    });
  };

  const handlePresetSelect = (presetId: string) => {
    const p = presets.find((x) => x.id === presetId);
    if (p) {
      setActivePreset(p);
      // Alle Felder aus dem Preset auto-pinnen
      const fields = Object.keys(p.daten || {}) as (keyof BaumFormData)[];
      setPinned(new Set(fields));
      setSnackbar({ text: `Preset "${p.name}" geladen`, severity: 'success' });
    }
  };

  const handlePresetSave = async (name: string) => {
    try {
      const daten: any = {};
      for (const field of pinned) {
        daten[field] = (lastForm as any)[field];
      }
      await presetsAPI.create({ name, projekt_id: id, daten });
      const ps = await presetsAPI.list(id);
      setPresets(ps);
      setSnackbar({ text: `Preset "${name}" gespeichert`, severity: 'success' });
    } catch (e: any) {
      setSnackbar({ text: e.response?.data?.error || 'Preset-Speichern fehlgeschlagen', severity: 'error' });
    }
  };

  const handleSave = async (
    data: BaumFormData,
    schaden: SchadenData[],
    mode: 'uebernehmen' | 'anpassen-erledigt'
  ) => {
    if (!gps.position || !id) return;
    const payload: any = {
      projekt_id: id,
      ...data,
      latitude: gps.position.latitude,
      longitude: gps.position.longitude,
      gps_quality: gps.position.quality,
      gps_accuracy_mm: gps.position.accuracy_mm,
      gps_satellites: gps.position.satellites || null,
      gps_hdop: gps.position.hdop || null,
      gps_recorded_at: gps.position.recorded_at,
      schaden: schaden.filter((s) => s.beschreibung),
    };
    // Leere Strings zu null
    for (const k of Object.keys(payload)) {
      if (payload[k] === '') payload[k] = null;
    }
    const created = await baeumeAPI.create(payload);
    setBaeume((b) => [created, ...b]);
    setLastForm(data);   // Auto-Memory
    setSnackbar({ text: `🌳 Baum ${data.baumnummer || ''} (${data.baumart}) erfasst`, severity: 'success' });
    if (mode === 'uebernehmen') {
      // Dialog bleibt eigentlich offen für nächste Erfassung – aber wir schließen und der User klickt erneut
      setDialogOpen(false);
      // Direkt wieder öffnen wäre zu schnell – User muss nochmal auf "HIER ERFASSEN" tippen
    } else {
      setDialogOpen(false);
    }
  };

  const handleExport = () => {
    if (!id) return;
    const url = exportAPI.csvUrl(id);
    const token = localStorage.getItem('bw_erfassung_token');
    // Token in URL ist nicht ideal, aber für Download via Browser einfacher.
    // Besser: Fetch mit Auth-Header → Blob → Download
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `${projekt?.name || 'projekt'}_export.csv`;
        a.click();
        URL.revokeObjectURL(a.href);
        setSnackbar({ text: 'CSV-Export erfolgreich', severity: 'success' });
      })
      .catch(() => setSnackbar({ text: 'Export fehlgeschlagen', severity: 'error' }));
    setMenuAnchor(null);
  };

  const fixColor: any = {
    rtk_fix: 'success',
    rtk_float: 'warning',
    gps: 'info',
    handy: 'default',
    none: 'default',
  }[gps.fixType] || 'default';

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}>
      {/* Header */}
      <AppBar position="static" elevation={1}>
        <Toolbar>
          <IconButton color="inherit" onClick={() => navigate('/')} edge="start">
            <ArrowBack />
          </IconButton>
          <Box sx={{ flexGrow: 1, ml: 1 }}>
            <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2 }}>
              {projekt?.name || 'Lade…'}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.85 }}>
              {baeume.length} Bäume erfasst
            </Typography>
          </Box>
          <IconButton color="inherit" onClick={(e) => setMenuAnchor(e.currentTarget)}>
            <MoreVert />
          </IconButton>
          <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
            <MenuItem onClick={handleExport}>
              <Download fontSize="small" sx={{ mr: 1 }} /> CSV exportieren
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* GPS-Status-Leiste */}
      <Box sx={{
        bgcolor: 'background.paper',
        px: 2, py: 1,
        borderBottom: '1px solid', borderColor: 'divider',
      }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          {gps.position ? (
            <>
              <Chip
                icon={<GpsFixed />}
                label={gps.fixType === 'handy'
                  ? `Handy-GPS · ±${(gps.position.accuracy_mm / 1000).toFixed(1)} m`
                  : `${gps.fixType.toUpperCase()} · ±${gps.position.accuracy_mm} mm`}
                color={fixColor}
                size="small"
              />
              {gps.position.satellites != null && (
                <Chip icon={<SatelliteAlt />} label={`${gps.position.satellites} Sat`} size="small" variant="outlined" />
              )}
            </>
          ) : (
            <Chip icon={<GpsNotFixed />} label="GPS sucht…" color="warning" size="small" />
          )}
          {gps.error && <Typography variant="caption" color="error">{gps.error}</Typography>}
        </Stack>
      </Box>

      {/* Karte */}
      <Box sx={{ flexGrow: 1, position: 'relative' }}>
        <MapContainer center={mapCenter} zoom={baeume.length > 0 ? 16 : 6} style={{ height: '100%', width: '100%' }}>
          <TileLayer
            attribution='&copy; OpenStreetMap'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <CenterMap position={gps.position} />
          {/* Eigene Position */}
          {gps.position && (
            <Marker
              position={[gps.position.latitude, gps.position.longitude]}
              icon={ownPositionIcon}
            >
              <Popup>Deine Position</Popup>
            </Marker>
          )}
          {/* Bäume */}
          {baeume.filter((b) => b.latitude && b.longitude).map((b) => (
            <Marker
              key={b.id}
              position={[Number(b.latitude), Number(b.longitude)]}
              icon={treeIcon}
            >
              <Popup>
                <strong>{b.baumnummer || '—'}</strong><br />
                {b.baumart}<br />
                {b.strasse && <>{b.strasse}<br /></>}
                <Typography variant="caption">
                  {b.gps_quality && `${b.gps_quality.toUpperCase()} · ±${b.gps_accuracy_mm}mm`}
                </Typography>
              </Popup>
            </Marker>
          ))}
        </MapContainer>

        {/* Floating Action Button für Erfassung */}
        <Fab
          color="primary"
          variant="extended"
          onClick={handleOpenDialog}
          disabled={!gps.position}
          sx={{
            position: 'absolute',
            bottom: 16, left: '50%', transform: 'translateX(-50%)',
            px: 4, py: 3,
            fontSize: '1rem',
            fontWeight: 700,
            boxShadow: 6,
            zIndex: 1000,
          }}
        >
          <AddLocation sx={{ mr: 1 }} />
          BAUM HIER ERFASSEN
        </Fab>

        {/* Re-Center Button */}
        <Fab
          size="small"
          color="default"
          sx={{ position: 'absolute', top: 16, right: 16, zIndex: 1000 }}
          onClick={() => {
            if (gps.position) {
              // Trick: re-mount CenterMap durch State-Change (oder direkt map ref)
              // Hier einfach: GPS noch mal triggern
              gps.stop(); gps.start();
            }
          }}
        >
          <MyLocation />
        </Fab>
      </Box>

      {/* Erfassungs-Dialog */}
      <ErfassungsDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        position={gps.position}
        initial={initialForm}
        initialSchaden={[]}
        pinned={pinned}
        onPinChange={handlePinChange}
        onSave={handleSave}
        presets={presets}
        onPresetSelect={handlePresetSelect}
        onPresetSave={handlePresetSave}
      />

      {/* Snackbar */}
      <Snackbar
        open={!!snackbar}
        autoHideDuration={3500}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        {snackbar ? <Alert severity={snackbar.severity}>{snackbar.text}</Alert> : <></>}
      </Snackbar>

      {error && (
        <Snackbar open autoHideDuration={6000} onClose={() => setError('')}>
          <Alert severity="error">{error}</Alert>
        </Snackbar>
      )}
    </Box>
  );
}
